import { useEffect, useState } from 'react';
import { Button, inputCls } from './ui';

const SEEN_KEY = 'nf_newsletter_seen';

/**
 * Popup newslettera pokazywany po 20 sekundach na stronie.
 * Zamknięcie zapamiętywane jest na czas sesji.
 *
 * Zdarzenia `newsletter_shown` i `newsletter_signup` wypychamy jawnie do
 * dataLayer w momencie, gdy faktycznie się dzieją — a nie przez trigger
 * Element Visibility w GTM obserwujący klasy `.newsletter-modal` /
 * `.newsletter-success`. Ten drugi sposób zależy od tego, czy GTM zdąży
 * zauważyć element wstawiony dynamicznie przez Reacta (wymaga „Observe DOM
 * changes”, progu widoczności, braku wcześniejszego zużycia „Once per
 * page”...) — więcej rzeczy, które mogą nie zagrać. Push do dataLayer nie
 * zależy od niczego poza tym, że ten kod się wykonał.
 *
 * Klasy `newsletter-modal` / `newsletter-submit` / `newsletter-success`
 * zostają w markupie — nadal przydatne jako stabilne selektory (np. do CSS,
 * testów, ewentualnego triggera Click na przycisku), tylko już nie jako
 * jedyne źródło sygnału „pokazano” / „zapisano”.
 */
export function NewsletterModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => {
      setOpen(true);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'newsletter_shown' });
    }, 20000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    sessionStorage.setItem(SEEN_KEY, '1');
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center" onClick={close}>
      {/* newsletter-modal — stabilny selektor, sygnał "pokazano" idzie teraz przez event newsletter_shown w dataLayer (patrz komentarz nad komponentem). */}
      <div
        className="newsletter-modal w-full max-w-md rounded-xl border border-[#26304d] bg-[#0f1526] p-5 fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-white">−10% na pierwsze zamówienie</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-slate-400">
              Zapisz się do newslettera i odbierz kod rabatowy.
            </p>
          </div>
          <button onClick={close} className="text-slate-500 hover:text-white" aria-label="Zamknij">
            ✕
          </button>
        </div>

        {done ? (
          // newsletter-success — stabilny selektor. Sygnał "faktycznie
          // zapisany" to teraz event newsletter_signup, wypychany niżej
          // dopiero PO przejściu walidacji — nie samo kliknięcie w przycisk,
          // które odpala się też przy błędnym adresie.
          <p className="newsletter-success mt-4 rounded-lg bg-[#22d3a6]/10 p-3 text-[13px] text-[#7ef0d0]">
            Dziękujemy. Kod <strong>LATO10</strong> jest już aktywny — wpisz go w koszyku.
          </p>
        ) : (
          <form
            className="mt-4 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!email.includes('@')) {
                setError('Podaj poprawny adres e-mail.');
                return;
              }
              setError('');
              setDone(true);
              sessionStorage.setItem(SEEN_KEY, '1');
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ event: 'newsletter_signup' });
            }}
          >
            <input
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="twoj@email.pl"
              aria-label="Adres e-mail"
            />
            <Button type="submit" className="newsletter-submit">
              Zapisz się
            </Button>
          </form>
        )}
        {error && <p className="mt-2 text-[12px] text-[#ffb0bb]">{error}</p>}
      </div>
    </div>
  );
}
